import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { Input, useDialog } from '@gokrew/ui';
import { ComboBox } from '@gokrew/ui';
import { MoneyField } from '@gokrew/ui/form';
import { FormikProvider, useFormik } from 'formik';
import { Trash2 } from 'lucide-react';
import {
  deleteCrmCompany,
  getCrmMembers,
  getCrmPartners,
  saveCrmCompany,
} from '@/api/crm';
import type { CrmCompany, CrmCompanyInput } from '@/api/crm';
import { useAuth } from '@/context/AuthContext';
import { CrmPartnerForm } from './CrmPartnerForm';
import { CrmLocationFields } from './CrmLocationFields';
import { CrmDeleteDialog } from './CrmDeleteDialog';
import { CRM_SIZES, CRM_SOURCES } from './crm-access';
import { personName } from './crm-format';
import { CrmButton, CrmRequired } from './CrmShared';
import styles from './Crm.module.css';

export const CrmCompanyEditor = ({
  company,
  dialogId,
  initialName = '',
  onSaved,
}: {
  company?: CrmCompany;
  dialogId?: string;
  /** Prefills the name of a new company, e.g. what was typed in a search. */
  initialName?: string;
  onSaved?: (company: CrmCompany) => void;
}) => {
  const { t, i18n } = useTranslation(TranslationNS_Admin.CompanyInfo);
  const { openDialog, closeDialog } = useDialog();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CrmCompanyInput>(
    company
      ? { ...company }
      : {
          name: initialName.trim(),
          ownerUserId: user?.id ?? null,
          companySize: 'unknown',
          estimatedEmployees: null,
          source: 'inbound',
          partnerId: null,
          country: 'Canada',
          city: '',
          province: '',
          product: 'hris',
          atsMrrCents: null,
          stage: 'new',
          notes: '',
          contacts: [],
        }
  );
  const amount = useFormik<{ mrr: number | '' }>({
    initialValues: {
      mrr: company?.atsMrrCents == null ? '' : company.atsMrrCents / 100,
    },
    onSubmit: () => {},
  });
  const manualMrrCents =
    amount.values.mrr === '' ? null : Math.round(amount.values.mrr * 100);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [addingPartner, setAddingPartner] = useState(false);
  const partners = useQuery({
    queryKey: ['crm-partners'],
    queryFn: getCrmPartners,
  });
  const members = useQuery({
    queryKey: ['crm', 'members'],
    queryFn: getCrmMembers,
  });
  const set = <K extends keyof CrmCompanyInput>(
    key: K,
    value: CrmCompanyInput[K]
  ) => setForm((old) => ({ ...old, [key]: value }));
  const needsPartner = ['broker', 'hr_consultant'].includes(form.source);
  const mrr =
    form.product === 'hris'
      ? form.estimatedEmployees === null
        ? null
        : form.estimatedEmployees * 495
      : manualMrrCents;
  const money = (cents: number | null) =>
    cents === null
      ? t('crm.unknown')
      : new Intl.NumberFormat(i18n.language, {
          style: 'currency',
          currency: 'CAD',
          currencyDisplay: 'narrowSymbol',
          maximumFractionDigits: 0,
        }).format(cents / 100);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (needsPartner && !form.partnerId) {
      setError(t('crm.partner_required'));
      return;
    }
    setBusy(true);
    try {
      const {
        name,
        companySize,
        estimatedEmployees,
        source,
        partnerId,
        country,
        city,
        province,
        product,
        stage,
        notes,
        contacts,
      } = form;
      const saved = await saveCrmCompany(
        {
          name: name.trim(),
          companySize,
          estimatedEmployees,
          source,
          partnerId: needsPartner ? partnerId : null,
          country,
          city,
          province,
          product,
          atsMrrCents: form.product === 'ats' ? manualMrrCents : null,
          stage,
          notes,
          contacts: contacts.map((c) => ({
            name: c.name.trim(),
            ...(c.email?.trim() ? { email: c.email.trim() } : {}),
            ...(c.phone ? { phone: c.phone } : {}),
            ...(c.title ? { title: c.title } : {}),
          })),
          ...(company ? { version: company.version } : {}),
        },
        company?.id
      );
      await queryClient.invalidateQueries({ queryKey: ['crm-companies'] });
      await queryClient.invalidateQueries({ queryKey: ['crm'] });
      onSaved?.(saved);
      if (dialogId) closeDialog(dialogId);
    } catch (err) {
      setError(
        t(
          (err as { response?: { status?: number } }).response?.status === 409
            ? 'crm.conflict'
            : 'crm.save_error'
        )
      );
    } finally {
      setBusy(false);
    }
  };
  const choice = (
    key: 'companySize' | 'source' | 'stage' | 'product',
    values: string[],
    prefix?: string
  ) => (
    <label>
      {t(`crm.${key}`)}
      <ComboBox
        allowUnselect={false}
        value={form[key]}
        options={values.map((value) => ({
          value,
          label: prefix
            ? t(`crm.${prefix}.${value}`)
            : value === 'unknown'
              ? t('crm.unknown')
              : value,
        }))}
        onChange={(v) => {
          if (v) set(key, v);
        }}
      />
    </label>
  );
  return (
    <FormikProvider value={amount}>
      <div className={styles.editor}>
        <form onSubmit={submit} className={styles.form}>
          <fieldset disabled={busy} className={styles.grid}>
            <label>
              <span>
                {t('crm.name')}
                <CrmRequired />
              </span>
              <Input
                required
                autoFocus
                maxLength={200}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </label>
            {choice('companySize', CRM_SIZES)}
            <label>
              {t('crm.estimatedEmployees')}
              <Input
                type="number"
                min={0}
                max={10000000}
                step={1}
                value={form.estimatedEmployees ?? ''}
                onChange={(e) =>
                  set(
                    'estimatedEmployees',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </label>
            <CrmLocationFields
              value={form}
              onChange={(next) => setForm((old) => ({ ...old, ...next }))}
            />
            <label>
              {t('crm.owner')}
              <ComboBox
                value={form.ownerUserId ?? ''}
                placeholder={t('crm.unknown')}
                options={(members.data ?? []).map((member) => ({
                  value: member.userId,
                  label: personName(member),
                }))}
                onChange={(v) => set('ownerUserId', v || null)}
              />
            </label>
            {choice('source', CRM_SOURCES, 'sources')}
            {needsPartner && (
              <label>
                <span>
                  {t('crm.partner')}
                  <CrmRequired />
                </span>
                <ComboBox
                  allowUnselect={false}
                  value={form.partnerId ?? ''}
                  placeholder={t('crm.select_partner')}
                  options={(partners.data ?? []).map((p) => ({
                    value: p.id,
                    label: `${p.name} (${t(`crm.sources.${p.kind}`)})`,
                  }))}
                  onChange={(v) => set('partnerId', v ?? null)}
                />
              </label>
            )}
            {needsPartner && (
              <CrmButton
                type="button"
                variant="outline"
                onClick={() => setAddingPartner((v) => !v)}
              >
                {t('crm.add_partner')}
              </CrmButton>
            )}
            {choice('product', ['hris', 'ats'], 'products')}
            {form.product === 'ats' && (
              <MoneyField name="mrr" label={t('crm.manual_mrr')} suffix="$" />
            )}
          </fieldset>
          <div className={styles.revenue}>
            <span>
              {t('crm.mrr')}
              <strong>{money(mrr)}</strong>
            </span>
            <span>
              {t('crm.arr')}
              <strong>{money(mrr === null ? null : mrr * 12)}</strong>
            </span>
          </div>
          <p className={styles.muted}>
            {t('crm.workspace.company_contacts_hint')}
          </p>
          <label>
            {t('crm.notes')}
            <textarea
              maxLength={10000}
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </label>
          {error && (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          )}
          <div className={styles.actions}>
            {company && (
              <CrmButton
                type="button"
                variant="destructive"
                IconPrefix={Trash2}
                className={styles.editorDelete}
                disabled={busy}
                onClick={() =>
                  openDialog({
                    title: t('crm.delete_title'),
                    content: (
                      <CrmDeleteDialog
                        description={t('crm.delete_description', {
                          name: company.name,
                        })}
                        conflictKey="crm.delete_conflict"
                        onDelete={() => deleteCrmCompany(company)}
                        editorDialogId={dialogId}
                      />
                    ),
                  })
                }
              >
                {t('crm.delete')}
              </CrmButton>
            )}
            <CrmButton
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => dialogId && closeDialog(dialogId)}
            >
              {t('crm.cancel')}
            </CrmButton>
            <CrmButton
              type="submit"
              variant="adminPrimary"
              disabled={busy || !form.name.trim() || addingPartner}
            >
              {t(busy ? 'crm.saving' : 'crm.save')}
            </CrmButton>
          </div>
        </form>
        {addingPartner && (
          <section className={styles.partnerForm}>
            <h3>{t('crm.add_partner')}</h3>
            <CrmPartnerForm
              onAdded={(partner) => {
                void queryClient.invalidateQueries({
                  queryKey: ['crm-partners'],
                });
                set('partnerId', partner.id);
                setAddingPartner(false);
              }}
            />
          </section>
        )}
      </div>
    </FormikProvider>
  );
};
